import React, { createContext, useContext, useEffect, useState } from "react";
import { child, get, ref, set, runTransaction, push } from "firebase/database";
import { database } from "../firebaseConfig";
import { useAuth } from "./auth";

const GameContext = createContext();
export const useGame = () => useContext(GameContext);

export const GameProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const uid = currentUser?.auth?.uid;
  const VERSION = process.env.REACT_APP_VERSION || "v1";

  const [loading, setLoading] = useState(true);
  const [deaths, setDeaths] = useState(0);
  const [escapes, setEscapes] = useState(0);

  useEffect(() => {
    const loadGameState = async () => {
      try {
        setLoading(true);
        if (currentUser) {
          const dbRef = ref(database);
          // Grab the user’s current game data
          const snapshotDeaths = await get(
            child(dbRef, `users/${uid}/game/${VERSION}/deaths/total`)
          );
          const snapshotEscapes = await get(
            child(dbRef, `users/${uid}/game/${VERSION}/escapes/total`)
          );

          // If these fields don’t exist, .val() might be null
          const dbDeaths = snapshotDeaths.val() || 0;
          const dbEscapes = snapshotEscapes.val() || 0;

          setDeaths(dbDeaths);
          setEscapes(dbEscapes);
        }
      } catch (error) {
        console.error("Error loading game data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadGameState();
  }, [currentUser, uid, VERSION]);

  // recordDeath using a transaction on the "total" field
  const recordDeath = async () => {
    if (!currentUser) return;

    try {
      const dbRef = ref(database);
      // For convenience, gather a displayName from your Auth context/data object:
      const displayName = currentUser?.data?.displayName || "Unknown";
      const timestamp = new Date().toISOString();

      // 1) Atomically increment "deaths/total"
      const totalRef = child(
        dbRef,
        `users/${uid}/game/${VERSION}/deaths/total`
      );
      const transactionResult = await runTransaction(totalRef, (currentVal) => {
        return (currentVal || 0) + 1;
      });

      if (!transactionResult.committed) {
        console.warn("Death transaction not committed, aborting.");
        return;
      }

      // 2) new total in the snapshot
      const updatedTotal = transactionResult.snapshot.val();

      // 3) create a unique death# key under that user
      const newDeathKey = `death${updatedTotal}`;
      await set(
        child(dbRef, `users/${uid}/game/${VERSION}/deaths/${newDeathKey}`),
        timestamp
      );

      // 4) Also increment the globalGameStats/deaths
      const globalDeathsRef = ref(database, "globalGameStats/deaths");
      await runTransaction(globalDeathsRef, (currentTotal) => {
        return (currentTotal || 0) + 1;
      });

      // 5) Push a record to the global "/deaths" array
      //    This helps keep track of the event globally in chronological order
      const deathsRef = ref(database, "deaths");
      await push(deathsRef, {
        uid,
        displayName,
        timestamp,
      });

      // 6) Update local state so the UI reflects the new total
      setDeaths(updatedTotal);
    } catch (error) {
      console.error("Error recording death:", error);
    }
  };

  const recordEscape = async () => {
    if (!currentUser) return;

    try {
      const dbRef = ref(database);
      const displayName = currentUser?.data?.displayName || "Unknown";
      const timestamp = new Date().toISOString();

      // 1) Increment local count
      const newCount = escapes + 1;
      const escapeKey = `escape${newCount}`;

      // 2) Check if the escapeKey already exists (avoid overwriting)
      const escapeSnapshot = await get(
        child(dbRef, `users/${uid}/game/${VERSION}/escapes/${escapeKey}`)
      );
      if (escapeSnapshot.exists()) {
        console.warn(`Escape key ${escapeKey} already exists.`);
        return;
      }

      // 3) Update user’s escapes total and set the timestamp
      setEscapes(newCount);
      await set(
        child(dbRef, `users/${uid}/game/${VERSION}/escapes/total`),
        newCount
      );
      await set(
        child(dbRef, `users/${uid}/game/${VERSION}/escapes/${escapeKey}`),
        timestamp
      );

      // 4) Update the global escapes counter via a transaction
      const globalEscapesRef = ref(database, "globalGameStats/escapes");
      await runTransaction(globalEscapesRef, (currentTotal) => {
        return (currentTotal || 0) + 1;
      });

      // 5) Push a record to the global "/escapes" array
      const escapesRef = ref(database, "escapes");
      await push(escapesRef, {
        uid,
        displayName,
        timestamp,
      });
    } catch (error) {
      console.error("Error recording escape:", error);
    }
  };

  const value = {
    loading,
    deaths,
    escapes,
    recordDeath,
    recordEscape,
  };

  return (
    <GameContext.Provider value={value}>
      {!loading && children}
    </GameContext.Provider>
  );
};
